import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { APPLICATION_TYPES, EMPTY_STEP_STATE, INTENT_TYPES, ONBOARDING_STORAGE_KEY, STEP_MODE, STEP_STATUS } from '../Config/Onboarding/constant';
import {  getStepFlowByMode, getStepFlowForIntent, IOT_HARDWARE_STEPS } from '../Config/Onboarding/stepFlow';
import {  createInitialState } from '../Config/Onboarding/initialState';

const OnboardingContext = createContext();

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

const initialState = createInitialState();

/**
 * Syncs the saved steps registry with a new step order.
 *
 * Does three things:
 * 1. Guards against an empty stepOrder being passed in
 * 2. Adds any new steps (from stepOrder) that don't exist yet in saved state
 * 3. Prunes stale steps that no longer belong in the current flow
 *    (only removes steps >= pruneFrom to preserve common steps 1–3)
 *
 * @param {Object} existingSteps - The current saved steps registry { [stepId]: StepState }
 * @param {number[]} newStepOrder - The correct step IDs for the current flow
 * @param {number} pruneFrom - Steps >= this value are eligible for pruning (default: 4)
 * @returns {Object} - A clean, reconciled steps registry
 */

const reconcileSteps = (existingSteps, newStepOrder, pruneFrom = 4) => {
  if (!newStepOrder || newStepOrder.length === 0) {
    console.warn('[reconcileSteps] Empty stepOrder passed — skipping reconciliation');
    return existingSteps;
  }

  const steps = { ...existingSteps };

  newStepOrder.forEach(stepId => {
    if (!steps[stepId]) steps[stepId] = { ...EMPTY_STEP_STATE };
  });

  Object.keys(steps).forEach(stepId => {
    const num = parseInt(stepId);
    if (num >= pruneFrom && !newStepOrder.includes(num)) delete steps[stepId];
  });

  return steps;
};

/** Derive flow.completedSteps from engine.steps — single source of truth is step status */
const syncCompletedSteps = (stepOrder, steps) =>
  stepOrder.filter((stepId) => steps[stepId]?.status === STEP_STATUS.COMPLETED);

/** On back: reopen target step, clear progress on later steps */
const rollbackStepsFrom = (steps, stepOrder, targetStepId) => {
  const targetIndex = stepOrder.indexOf(targetStepId);
  if (targetIndex === -1) return steps;

  const updated = { ...steps };
  stepOrder.forEach((stepId, index) => {
    if (index < targetIndex) return;
    const existing = updated[stepId] || { ...EMPTY_STEP_STATE };
    updated[stepId] = {
      ...existing,
      status:
        index === targetIndex ? STEP_STATUS.IN_PROGRESS : STEP_STATUS.NOT_STARTED,
    };
  });
  return updated;
};

const stateFromStorage = () => {
  try {
    const saved = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!saved) return initialState;

    const parsed = JSON.parse(saved);
    if (!parsed.engine) return initialState;

    const appType          = parsed.data?.project?.applicationType;
    const intent           = parsed.engine?.intent;
    const testCreationMode = parsed.data?.testCreationMode;

    let finalFlow;

    if (appType === APPLICATION_TYPES.IOT_HARDWARE) {
      finalFlow = IOT_HARDWARE_STEPS;
    } else if (intent) {
      const baseFlow = getStepFlowForIntent(intent);
      finalFlow = testCreationMode ? getStepFlowByMode(baseFlow, testCreationMode) : baseFlow;
    } else if (appType) {
      finalFlow = getStepFlowForIntent(INTENT_TYPES.MANUAL_TO_AUTOMATION);
    }
    // ✅ If none of the above matched, finalFlow stays undefined → return parsed as-is

    if (finalFlow) {
      const newStepOrder = finalFlow.map(s => s.id);
      parsed.engine.flow.stepOrder = newStepOrder;
      parsed.engine.steps = reconcileSteps(parsed.engine.steps, newStepOrder);
    }

    const { stepOrder, currentStep } = parsed.engine.flow;
    if (stepOrder?.length && currentStep) {
      parsed.engine.steps = rollbackStepsFrom(
        parsed.engine.steps,
        stepOrder,
        currentStep
      );
    }
    if (stepOrder?.length) {
      parsed.engine.flow.completedSteps = syncCompletedSteps(
        stepOrder,
        parsed.engine.steps
      );
    }

    return parsed;

  } catch (error) {
    console.error('[Onboarding] Failed to restore state from storage:', error);
    return initialState;
  }
};




export const OnboardingProvider = ({ children }) => {
  const [state, setState] = useState(stateFromStorage);

  // Persist to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving onboarding state:', error);
    }
  }, [state]);


  /**
   * Initialize step order and step registry based on intent
   * Called when user selects intent in Step 1
   * IMPORTANT: Preserves any step status that was already set (e.g., after login)
   */
  const initializeStepFlow = useCallback((intent) => {
    setState(prev => {
      // If flow is already initialized for this intent, don't reset it
      if (prev.engine.intent === intent && prev.engine.flow.stepOrder.length > 0) {
        return prev; // Already initialized, preserve state
      }

      const stepFlow = getStepFlowForIntent(intent);
      const stepOrder = stepFlow.map(step => step.id);
      
      // Create step registry with initial status
      // Preserve any existing step statuses from previous calls (e.g., after login marked step 1 as COMPLETED)
      const steps = {};
      stepOrder.forEach(stepId => {
        const existingStep = prev.engine.steps[stepId];
        steps[stepId] = existingStep || {
          status: STEP_STATUS.NOT_STARTED,
          mode: null, // Will be set to CREATE for persisted steps
          isEditing: false,
          createdEntityId: null
        };
      });
      
      // Only set step 1 to IN_PROGRESS if it's NOT_STARTED (first time)
      if (!steps[1].status || steps[1].status === STEP_STATUS.NOT_STARTED) {
        steps[1].status = STEP_STATUS.IN_PROGRESS;
      }

      return {
        ...prev,
        engine: {
          ...prev.engine,
          intent,
          flow: {
            stepOrder,
            currentStep: 1,
            completedSteps: syncCompletedSteps(stepOrder, steps),
          },
          steps
        }
      };
    });
  }, []);

  /**
   * Move to next step using stepOrder
   * Only sets next step to IN_PROGRESS if it hasn't been visited yet (status === NOT_STARTED)
   */
  const nextStep = useCallback(() => {
    setState(prev => {
      const { engine: { flow } } = prev;
      const currentIndex = flow.stepOrder.indexOf(flow.currentStep);
      
      if (currentIndex === -1 || currentIndex >= flow.stepOrder.length - 1) {
        return prev; // Already at last step
      }

      const nextStepId = flow.stepOrder[currentIndex + 1];
      const steps = {
        ...prev.engine.steps,
        [flow.currentStep]: {
          ...prev.engine.steps[flow.currentStep],
          status: STEP_STATUS.COMPLETED,
        },
        [nextStepId]: {
          ...prev.engine.steps[nextStepId],
          status:
            !prev.engine.steps[nextStepId]?.status ||
            prev.engine.steps[nextStepId]?.status === STEP_STATUS.NOT_STARTED
              ? STEP_STATUS.IN_PROGRESS
              : prev.engine.steps[nextStepId].status,
        },
      };

      return {
        ...prev,
        engine: {
          ...prev.engine,
          flow: {
            ...flow,
            currentStep: nextStepId,
            completedSteps: syncCompletedSteps(flow.stepOrder, steps),
          },
          steps,
        },
      };
    });
  }, []);

  /**
   * Move to previous step using stepOrder
   */
  const previousStep = useCallback(() => {
    setState(prev => {
      const { engine: { flow } } = prev;
      const currentIndex = flow.stepOrder.indexOf(flow.currentStep);
      
      if (currentIndex <= 0) {
        return prev; // Already at first step
      }

      const prevStepId = flow.stepOrder[currentIndex - 1];
      const steps = rollbackStepsFrom(prev.engine.steps, flow.stepOrder, prevStepId);

      return {
        ...prev,
        engine: {
          ...prev.engine,
          flow: {
            ...flow,
            currentStep: prevStepId,
            completedSteps: syncCompletedSteps(flow.stepOrder, steps),
          },
          steps,
        },
      };
    });
  }, []);

  /**
   * Go to specific step (used for navigation)
   */
  const goToStep = useCallback((stepId) => {
    setState(prev => {
      const { engine: { flow } } = prev;
      
      if (!flow.stepOrder.includes(stepId)) {
        return prev;
      }

      return {
        ...prev,
        engine: {
          ...prev.engine,
          flow: {
            ...flow,
            currentStep: stepId
          }
        }
      };
    });
  }, []);

  /**
   * Update step flow based on test creation mode
   * Called when user selects a test creation mode in Step 5
   * This dynamically updates the step order and adds/removes steps
   * Supports 3 modes: AI (9 total steps), RECORDER (10 total steps), UI_DISCOVERY (12 total steps)
   */
const updateStepFlowByMode = useCallback((testCreationMode) => {
  setState(prev => {
    if (!prev.engine.intent) return prev;
    const baseFlow     = getStepFlowForIntent(prev.engine.intent);
    const newStepOrder = getStepFlowByMode(baseFlow, testCreationMode).map(s => s.id);
    const steps        = reconcileSteps(prev.engine.steps, newStepOrder, 6);
    return {
      ...prev,
      engine: {
        ...prev.engine,
        flow: {
          ...prev.engine.flow,
          stepOrder: newStepOrder,
          completedSteps: syncCompletedSteps(newStepOrder, steps),
        },
        steps,
      },
    };
  });
}, []);

  /**
   * Find the last in-progress step
   * Useful for redirecting when user navigates away and back
   * Returns the highest step ID with IN_PROGRESS status
   */
  const findLastInProgressStep = useCallback(() => {
    const inProgressSteps = state.engine.flow.stepOrder.filter(
      stepId => state.engine.steps[stepId]?.status === STEP_STATUS.IN_PROGRESS
    );
    return inProgressSteps.length > 0 ? inProgressSteps[inProgressSteps.length - 1] : null;
  }, [state.engine.flow.stepOrder, state.engine.steps]);

  /**
   * Mark step as completed
   */
  const markStepCompleted = useCallback((stepId) => {
    setState(prev => {
      const steps = {
        ...prev.engine.steps,
        [stepId]: {
          ...prev.engine.steps[stepId],
          status: STEP_STATUS.COMPLETED,
        },
      };

      return {
        ...prev,
        engine: {
          ...prev.engine,
          flow: {
            ...prev.engine.flow,
            completedSteps: syncCompletedSteps(prev.engine.flow.stepOrder, steps),
          },
          steps,
        },
      };
    });
  }, []);

  /**
   * Update step status
   */
  const setStepStatus = useCallback((stepId, status) => {
    setState(prev => {
      const steps = {
        ...prev.engine.steps,
        [stepId]: {
          ...prev.engine.steps[stepId],
          status,
        },
      };

      return {
        ...prev,
        engine: {
          ...prev.engine,
          flow: {
            ...prev.engine.flow,
            completedSteps: syncCompletedSteps(prev.engine.flow.stepOrder, steps),
          },
          steps,
        },
      };
    });
  }, []);

  /**
   * Set step mode (create or edit)
   */
  const setStepMode = useCallback((stepId, mode) => {
    setState(prev => ({
      ...prev,
      engine: {
        ...prev.engine,
        steps: {
          ...prev.engine.steps,
          [stepId]: {
            ...prev.engine.steps[stepId],
            mode
          }
        }
      }
    }));
  }, []);

  /**
   * Set step editing state
   */
  const setStepEditing = useCallback((stepId, isEditing, editingSection = null) => {
    setState(prev => ({
      ...prev,
      engine: {
        ...prev.engine,
        steps: {
          ...prev.engine.steps,
          [stepId]: {
            ...prev.engine.steps[stepId],
            isEditing,
            editingSection
          }
        }
      }
    }));
  }, []);

  /**
   * Set created entity ID (after successful CREATE API)
   */
  const setStepCreatedEntityId = useCallback((stepId, entityId) => {
    setState(prev => ({
      ...prev,
      engine: {
        ...prev.engine,
        steps: {
          ...prev.engine.steps,
          [stepId]: {
            ...prev.engine.steps[stepId],
            createdEntityId: entityId,
            mode: STEP_MODE.EDIT // Switch mode to EDIT after successful create
          }
        }
      }
    }));
  }, []);


  const setWorkspace = useCallback((workspace) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        workspace: { ...prev.data.workspace, ...workspace }
      }
    }));
  }, []);

  const setProject = useCallback((project) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        project: { ...prev.data.project, ...project }
      }
    }));
  }, []);

  const setTestSource = useCallback((testSource) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        testSource: { ...prev.data.testSource, ...testSource }
      }
    }));
  }, []);

  const setTestCases = useCallback((testCases) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        testCases
      }
    }));
  }, []);

  const setAutomationPrefs = useCallback((prefs) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        automationPrefs: { ...prev.data.automationPrefs, ...prefs }
      }
    }));
  }, []);

  const setGenerationJob = useCallback((job) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        generationJob: { ...prev.data.generationJob, ...job }
      }
    }));
  }, []);

  const setRunJob = useCallback((job) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        runJob: { ...prev.data.runJob, ...job }
      }
    }));
  }, []);

  const setTestCreationMode = useCallback((mode) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        testCreationMode: mode
      }
    }));
  }, []);

  const setUiMap = useCallback((uiMapData) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        uiMap: { ...prev.data.uiMap, ...uiMapData }
      }
    }));
  }, []);

  const setRecorder = useCallback((recorderData) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        recorder: { ...prev.data.recorder, ...recorderData }
      }
    }));
  }, []);

  const setUiDiscovery = useCallback((discoveryData) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        uiDiscovery: { ...prev.data.uiDiscovery, ...discoveryData }
      }
    }));
  }, []);

  const setIot = useCallback((iotData) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        iot: { ...prev.data.iot, ...iotData }
      }
    }));
  }, []);

  const setMigrate = useCallback((migrateData) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        migrate: { ...prev.data.migrate, ...migrateData }
      }
    }));
  }, []);

  const setSessionId = useCallback((sessionId) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        sessionId
      }
    }));
  }, []);

  const updateState = useCallback((section, data) => {
    setState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [section]: typeof data === 'function' ? data(prev.data[section]) : data
      }
    }));
  }, []);

  const resetOnboarding = useCallback(() => {
    setState(initialState);
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  }, []);

  /**
   * Restore wizard state from API response (draft recovery)
   * Called when user resumes an incomplete wizard
   * @param {Object} wizardData - The data from get_wizard_step API response
   * @param {Object} wizardData.engine - The engine state (intent, flow, steps)
   * @param {Object} wizardData.data - The data state (workspace, project, etc.)
   */
 const restoreWizardData = useCallback((wizardData) => {
  if (!wizardData || (!wizardData.engine && !wizardData.data)) {
    console.error('Invalid wizard data provided for restore');
    return;
  }

  setState(prev => {
    const incomingEngine = wizardData.engine || prev.engine;
    const incomingData = wizardData.data || prev.data;

    // Never overwrite a valid intent with null from API
    const resolvedIntent = incomingEngine.intent || prev.engine.intent;

    // Recompute stepOrder based on resolved intent + appType
    const appType = incomingData?.project?.applicationType;
    const testCreationMode = incomingData?.testCreationMode;

    let finalFlow;
    if (appType === APPLICATION_TYPES.IOT_HARDWARE) {
      finalFlow = IOT_HARDWARE_STEPS;
    } else if (resolvedIntent) {
  const baseFlow = getStepFlowForIntent(resolvedIntent);
  finalFlow = testCreationMode
    ? getStepFlowByMode(baseFlow, testCreationMode)
    : baseFlow;
    } else if (appType) {
      // ✅ Same fallback here
      finalFlow = getStepFlowForIntent(INTENT_TYPES.MANUAL_TO_AUTOMATION);
    } else {
      finalFlow = null;
    }

    const resolvedStepOrder = finalFlow
      ? finalFlow.map(s => s.id)
      : incomingEngine.flow?.stepOrder || prev.engine.flow.stepOrder;

    const savedCurrentStep = incomingEngine.flow?.currentStep;
    const resolvedCurrentStep = resolvedStepOrder.includes(savedCurrentStep)
      ? savedCurrentStep
      : resolvedStepOrder[0];

    const steps = reconcileSteps(incomingEngine.steps ?? {}, resolvedStepOrder);
    const stepsAligned = rollbackStepsFrom(steps, resolvedStepOrder, resolvedCurrentStep);

    const restoredState = {
      engine: {
        ...incomingEngine,
        intent: resolvedIntent,
        flow: {
          ...incomingEngine.flow,
          stepOrder: resolvedStepOrder,
          currentStep: resolvedCurrentStep,
          completedSteps: syncCompletedSteps(resolvedStepOrder, stepsAligned),
        },
        steps: stepsAligned,
      },
      data: incomingData
    };

    return restoredState;
  });
}, []);

  // Helper methods for step tracking
  const getCompletedStepsCount = useCallback(() => {
    const { stepOrder } = state.engine.flow;
    return syncCompletedSteps(stepOrder, state.engine.steps).length;
  }, [state.engine.flow, state.engine.steps]);

  const getTotalSteps = useCallback(() => {
    // Return total number of steps in current flow
    return state.engine.flow.stepOrder.length || 0;
  }, [state.engine.flow.stepOrder]);

const updateStepFlowByAppType = useCallback((applicationType) => {
  setState(prev => {
    const intent = prev.engine.intent;
    let newStepFlow;

    if (applicationType === APPLICATION_TYPES.IOT_HARDWARE) {
      newStepFlow = IOT_HARDWARE_STEPS;
    } else if (intent) {
      newStepFlow = getStepFlowForIntent(intent);
    } else {
      // ✅ Fallback: intent not yet set, use default web/api flow
      newStepFlow = getStepFlowForIntent(INTENT_TYPES.MANUAL_TO_AUTOMATION);
    }

    const newStepOrder = newStepFlow.map(step => step.id);
    const steps = reconcileSteps(prev.engine.steps, newStepOrder);

    return {
      ...prev,
      engine: {
        ...prev.engine,
        flow: {
          ...prev.engine.flow,
          stepOrder: newStepOrder,
          completedSteps: syncCompletedSteps(newStepOrder, steps),
        },
        steps
      },
      data: {
        ...prev.data,
        project: {
          ...prev.data.project,
          applicationType
        }
      }
    };
  });
}, []);

  // =========================================================================
  // BACKWARD COMPATIBILITY ACCESSORS
  // =========================================================================

  // For backward compatibility with existing components
  const currentStep = state.engine.flow.currentStep;
  const completedSteps = state.engine.flow.completedSteps;
  const intent = state.engine.intent;

  const value = {
    // New engine API
    engine: state.engine,
    initializeStepFlow,
    markStepCompleted,
    setStepStatus,
    setStepMode,
    setStepEditing,
    setStepCreatedEntityId,
    updateStepFlowByMode,
    findLastInProgressStep,

    // Data accessors
    data: state.data,

    // Backward compatibility
    state,
    currentStep,
    completedSteps,
    intent,
    workspace: state.data.workspace,
    project: state.data.project,
    testSource: state.data.testSource,
    testCreationMode: state.data.testCreationMode,
    uiMap: state.data.uiMap,
    recorder: state.data.recorder,
    uiDiscovery: state.data.uiDiscovery,
    iot: state.data.iot,
    migrate: state.data.migrate,
    testCases: state.data.testCases,
    automationPrefs: state.data.automationPrefs,
    generationJob: state.data.generationJob,
    runJob: state.data.runJob,
    sessionId: state.data.sessionId,

    // Actions
    goToStep,
    nextStep,
    previousStep,
    updateState,
    setIntent: initializeStepFlow, // New intent sets flow
    setWorkspace,
    setProject,
    setTestSource,
    setTestCreationMode,
    setUiMap,
    setRecorder,
    setUiDiscovery,
    setIot,
    setMigrate,
    setTestCases,
    setAutomationPrefs,
    setGenerationJob,
    setRunJob,
    setSessionId,
    resetOnboarding,
    restoreWizardData,
    getCompletedStepsCount,
    getTotalSteps,
    updateStepFlowByAppType
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext;